import { ReactNode, useEffect } from 'react';

import { SwipeableDrawer } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';

import { useUiActionsStore } from '@src/stores';
import { useBridgeModal } from '@src/hooks/useBridgeModal';

import OButton from '../OButton';
import styles from './OSwipeableDrawer.module.scss';

interface OSwipeableDrawerProps {
  children: ReactNode;
  isOpen: boolean;
  classes?: {
    container?: string;
    body?: string;
    header?: string;
    closeIcon?: string;
    closeIconWrapper?: string;
    title?: string;
  };
  onClose: () => void;
  hasBackdrop?: boolean;
  title?: string;
  applyLabel?: string;
  cancelLabel?: string;
  onApply?: () => void;
  onCancel?: () => void;
}

const OSwipeableDrawer = ({
  children,
  isOpen,
  onClose,
  title,
  hasBackdrop,
  applyLabel = 'Apply',
  cancelLabel = 'Cancel',
  onApply,
  onCancel,
  classes = {},
}: OSwipeableDrawerProps) => {
  useBridgeModal(isOpen);

  const { hideMobileDrawer, setHideMobileDrawer } = useUiActionsStore(
    useShallow((state) => ({
      hideMobileDrawer: state.hideMobileDrawer,
      setHideMobileDrawer: state.setHideMobileDrawer,
    }))
  );

  useEffect(() => {
    if (hideMobileDrawer) {
      onClose();
      setHideMobileDrawer(false);
    }
  }, [hideMobileDrawer, onClose, setHideMobileDrawer]);

  return (
    <SwipeableDrawer
      id="swipeable-drawer"
      anchor="bottom"
      onClose={onClose}
      onOpen={() => {}}
      disableBackdropTransition={!hasBackdrop}
      open={!!isOpen}
      PaperProps={{
        style: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: '20px 0',
          height: 'auto',
          background: 'var(--o-modal-bg)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'var(--drawer-backdrop-color)',
          },
        },
      }}
    >
      <div className={`${styles.drawerHeader} ${title ? styles.withTitle : ''}`} id="swipeable-drawer-header">
        <div className={styles.dragHandle} />
        <div className={styles.drawerTitleRow} id="swipeable-drawer-title-row">
          <div className={styles.leftActions} id="swipeable-drawer-left-actions">
            {onCancel && (
              <OButton
                text={cancelLabel}
                onClick={onCancel}
                variant="primary"
                outlined
                size="x-small"
                className={styles.actionBtn}
              />
            )}
          </div>
          <div className={classes.title || styles.title} id="swipeable-drawer-title">
            {title}
          </div>
          <div className={styles.rightActions} id="swipeable-drawer-right-actions">
            {onApply && (
              <OButton
                text={applyLabel}
                onClick={onApply}
                variant="primary"
                outlined
                size="x-small"
                className={styles.actionBtn}
                id="swipeable-drawer-apply-btn"
              />
            )}
          </div>
        </div>
      </div>

      <div>{children}</div>
    </SwipeableDrawer>
  );
};

export default OSwipeableDrawer;
